import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, MapPin, Search, Filter, Grid, List, ChevronDown,
  Users, Clock, Tag, ArrowRight, X, Ticket, Share2, 
  MessageCircle, Twitter, Facebook, Copy, Check, Loader2
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default event images by category
const DEFAULT_EVENT_IMAGES = {
  tournament: 'https://images.unsplash.com/photo-1461896836934- voices-1024x683.jpg?w=800&h=600&fit=crop',
  conference: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop',
  workshop: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop',
  festival: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&h=600&fit=crop',
  training: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800&h=600&fit=crop',
  networking: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop',
  exhibition: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=800&h=600&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&h=600&fit=crop',
  golf: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&h=600&fit=crop',
  basketball: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&h=600&fit=crop',
  football: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&h=600&fit=crop',
  family: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&h=600&fit=crop',
  wellness: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&h=600&fit=crop',
  default: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop'
};

// Get appropriate image based on event name/type
const getEventImage = (event) => {
  if (event.hero_image_url) return event.hero_image_url;
  
  const nameLower = event.name?.toLowerCase() || '';
  const typeLower = event.event_type?.toLowerCase() || '';
  
  // Check name for keywords
  if (nameLower.includes('golf') || nameLower.includes('somabay')) return DEFAULT_EVENT_IMAGES.golf;
  if (nameLower.includes('basketball')) return DEFAULT_EVENT_IMAGES.basketball;
  if (nameLower.includes('football') || nameLower.includes('soccer')) return DEFAULT_EVENT_IMAGES.football;
  if (nameLower.includes('family') || nameLower.includes('kcc')) return DEFAULT_EVENT_IMAGES.family;
  if (nameLower.includes('wellness') || nameLower.includes('mental') || nameLower.includes('health')) return DEFAULT_EVENT_IMAGES.wellness;
  if (nameLower.includes('championship') || nameLower.includes('league')) return DEFAULT_EVENT_IMAGES.sports;
  
  // Check event type
  if (DEFAULT_EVENT_IMAGES[typeLower]) return DEFAULT_EVENT_IMAGES[typeLower];
  
  return DEFAULT_EVENT_IMAGES.default;
};

// Format price with currency
const formatPrice = (price, currency = 'KES') => {
  if (price === 0) return 'Free';
  return `${currency} ${price.toLocaleString()}`;
};

// Check if event is within 7 days
const isEventSoon = (startDate) => {
  if (!startDate) return false;
  const eventDate = new Date(startDate);
  const now = new Date();
  const diffTime = eventDate - now;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  return diffDays > 0 && diffDays <= 7;
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target - now;

      if (diff <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  if (isExpired) {
    return (
      <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg px-3 py-2 text-center">
        <span className="text-yellow-400 text-xs font-medium">Event Started!</span>
      </div>
    );
  }

  return (
    <div className="bg-primary/10 border border-primary/30 rounded-lg px-3 py-2" data-testid="countdown-timer">
      <div className="text-[10px] text-primary/70 font-medium mb-1 text-center">STARTS IN</div>
      <div className="flex items-center justify-center gap-1 text-white">
        {timeLeft.days > 0 && (
          <>
            <div className="text-center">
              <span className="text-lg font-bold text-primary">{timeLeft.days}</span>
              <span className="text-[10px] text-white/60 ml-0.5">d</span>
            </div>
            <span className="text-primary/50">:</span>
          </>
        )}
        <div className="text-center">
          <span className="text-lg font-bold text-primary">{String(timeLeft.hours).padStart(2, '0')}</span>
          <span className="text-[10px] text-white/60 ml-0.5">h</span>
        </div>
        <span className="text-primary/50">:</span>
        <div className="text-center">
          <span className="text-lg font-bold text-primary">{String(timeLeft.minutes).padStart(2, '0')}</span>
          <span className="text-[10px] text-white/60 ml-0.5">m</span>
        </div>
        <span className="text-primary/50">:</span>
        <div className="text-center">
          <span className="text-lg font-bold text-primary">{String(timeLeft.seconds).padStart(2, '0')}</span>
          <span className="text-[10px] text-white/60 ml-0.5">s</span>
        </div>
      </div>
    </div>
  );
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Share Menu Component
const ShareMenu = ({ event, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const eventUrl = `${window.location.origin}/events/${event.slug}`;
  const shareText = `Check out "${event.name}" on Michezonet! ${formatDate(event.start_date)} at ${event.venue_name || 'TBA'}`;
  
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + eventUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(eventUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}&quote=${encodeURIComponent(shareText)}`
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      className="absolute right-0 top-full mt-2 z-50 bg-zinc-900 border border-zinc-700 rounded-xl p-3 shadow-xl min-w-[200px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-xs text-white/50 mb-2 font-medium">Share this event</div>
      
      <a
        href={shareLinks.whatsapp}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-white"
        data-testid={`share-whatsapp-${event.slug}`}
      >
        <MessageCircle className="w-4 h-4 text-green-500" />
        <span className="text-sm">WhatsApp</span>
      </a>
      
      <a
        href={shareLinks.twitter}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-white"
        data-testid={`share-twitter-${event.slug}`}
      >
        <Twitter className="w-4 h-4 text-sky-400" />
        <span className="text-sm">Twitter</span>
      </a>
      
      <a
        href={shareLinks.facebook}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-white"
        data-testid={`share-facebook-${event.slug}`}
      >
        <Facebook className="w-4 h-4 text-blue-500" />
        <span className="text-sm">Facebook</span>
      </a>
      
      <div className="border-t border-zinc-700 my-2" />
      
      <button
        onClick={copyLink}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors text-white w-full"
        data-testid={`share-copy-${event.slug}`}
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 text-green-500" />
            <span className="text-sm text-green-500">Copied!</span>
          </>
        ) : (
          <>
            <Copy className="w-4 h-4 text-white/70" />
            <span className="text-sm">Copy link</span>
          </>
        )}
      </button>
    </motion.div>
  );
};

// Quick Registration Modal
const QuickRegisterModal = ({ event, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && event) {
      fetchTicketTypes();
    }
  }, [isOpen, event]);

  const fetchTicketTypes = async () => {
    try {
      const res = await fetch(`${API}/universal-events/events/${event.id}/ticket-types`);
      if (res.ok) {
        const data = await res.json();
        setTicketTypes(data);
        if (data.length > 0) {
          setSelectedTicket(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching ticket types:', err);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!selectedTicket) newErrors.ticket = 'Please select a ticket type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      // Create order
      const orderRes = await fetch(`${API}/universal-events/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: event.id,
          buyer_name: formData.name,
          buyer_email: formData.email,
          buyer_phone: formData.phone,
          buyer_country: 'Kenya',
          items: [{
            ticket_type_id: selectedTicket,
            quantity: 1,
            attendee_details: [{ name: formData.name, email: formData.email }]
          }]
        })
      });

      if (orderRes.ok) {
        const order = await orderRes.json();
        toast.success('Registration successful! Check your email for confirmation.');
        onClose();
        // Redirect to order confirmation or event detail
        window.location.href = `/events/${event.slug}?order=${order.id}`;
      } else {
        const errData = await orderRes.json();
        toast.error(errData.detail || 'Registration failed. Please try again.');
      }
    } catch (err) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedTicketData = ticketTypes.find(t => t.id === selectedTicket);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-zinc-900 border border-zinc-700 rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Quick Register</h3>
            <button onClick={onClose} className="text-white/60 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-white/60 mt-1">{event.name}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Ticket Selection */}
          {ticketTypes.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Select Ticket</label>
              <div className="space-y-2">
                {ticketTypes.map(ticket => (
                  <label
                    key={ticket.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedTicket === ticket.id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="ticket"
                        checked={selectedTicket === ticket.id}
                        onChange={() => setSelectedTicket(ticket.id)}
                        className="w-4 h-4 text-primary"
                      />
                      <div>
                        <div className="text-white font-medium">{ticket.name}</div>
                        {ticket.description && (
                          <div className="text-xs text-white/50">{ticket.description}</div>
                        )}
                      </div>
                    </div>
                    <span className="text-primary font-semibold">
                      {ticket.price > 0 ? `KES ${ticket.price.toLocaleString()}` : 'Free'}
                    </span>
                  </label>
                ))}
              </div>
              {errors.ticket && <p className="text-red-500 text-xs mt-1">{errors.ticket}</p>}
            </div>
          ) : (
            <div className="text-center py-4 text-white/60">
              <Ticket className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No tickets available for this event</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-primary"
              placeholder="John Doe"
              data-testid="quick-reg-name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-primary"
              placeholder="john@example.com"
              data-testid="quick-reg-email"
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-primary"
              placeholder="+254 712 345 678"
              data-testid="quick-reg-phone"
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          {/* Summary */}
          {selectedTicketData && (
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Total</span>
                <span className="text-white font-semibold">
                  {selectedTicketData.price > 0 
                    ? `KES ${selectedTicketData.price.toLocaleString()}` 
                    : 'Free'}
                </span>
              </div>
            </div>
          )}

          {/* Submit */}
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || ticketTypes.length === 0}
            data-testid="quick-reg-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Ticket className="w-4 h-4 mr-2" />
                Register Now
              </>
            )}
          </Button>

          <p className="text-xs text-center text-white/50">
            For more options, <Link to={`/events/${event.slug}`} className="text-primary hover:underline">view full event page</Link>
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Event Card Component
const EventCard = ({ event, viewMode = 'grid', onQuickRegister }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  
  const lowestPrice = event.ticket_types?.reduce((min, tt) => 
    tt.price < min ? tt.price : min, 
    event.ticket_types[0]?.price || 0
  ) || 0;

  const isGridView = viewMode === 'grid';
  const eventImage = getEventImage(event);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group bg-card-bg border border-border rounded-xl overflow-hidden hover:border-primary/40 transition-all duration-300 ${
        isGridView ? '' : 'flex'
      }`}
      data-testid={`event-card-${event.slug}`}
    >
      {/* Event Image */}
      <div className={`relative overflow-hidden ${isGridView ? 'h-48' : 'w-48 h-full shrink-0'}`}>
        <img
          src={eventImage}
          alt={event.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = DEFAULT_EVENT_IMAGES.default;
          }}
        />
        
        {/* Share Button */}
        <div className="absolute top-3 right-3 z-10">
          <div className="relative">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowShareMenu(!showShareMenu);
              }}
              className={`p-2 rounded-full transition-all ${
                showShareMenu 
                  ? 'bg-primary text-white' 
                  : 'bg-black/50 text-white hover:bg-black/70'
              }`}
              data-testid={`share-btn-${event.slug}`}
            >
              <Share2 className="w-4 h-4" />
            </button>
            
            <AnimatePresence>
              {showShareMenu && (
                <ShareMenu 
                  event={event} 
                  isOpen={showShareMenu} 
                  onClose={() => setShowShareMenu(false)} 
                />
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Category Badge */}
        {event.event_type && (
          <span className="absolute top-3 left-3 px-2 py-1 bg-primary text-white text-xs font-medium rounded capitalize">
            {event.event_type}
          </span>
        )}
        
        {/* Featured Badge */}
        {event.is_featured && (
          <span className="absolute bottom-3 left-3 px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">
            Featured
          </span>
        )}
      </div>

      {/* Event Details */}
      <div className={`p-4 flex flex-col ${isGridView ? '' : 'flex-1'}`}>
        <div className="flex-1">
          <h3 className="font-semibold text-white text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
            {event.name}
          </h3>
          
          {event.short_description && (
            <p className="text-white/60 text-sm mb-3 line-clamp-2">
              {event.short_description}
            </p>
          )}
          
          <div className="space-y-2 text-sm text-white/70">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span>{formatDate(event.start_date)}</span>
            </div>
            
            {event.venue_name && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="line-clamp-1">{event.venue_name}, {event.city}</span>
              </div>
            )}
            
            {event.is_virtual && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                <span>Virtual Event</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Countdown Timer for events within 7 days */}
        {isEventSoon(event.start_date) && (
          <div className="mt-3">
            <CountdownTimer targetDate={event.start_date} />
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-2">
            <Ticket className="w-4 h-4 text-primary" />
            <span className="font-semibold text-white">
              {lowestPrice > 0 ? `From ${formatPrice(lowestPrice)}` : 'Free'}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Quick Register Button */}
            <Button 
              variant="default" 
              size="sm" 
              className="bg-primary hover:bg-primary/90"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickRegister(event);
              }}
              data-testid={`quick-register-${event.slug}`}
            >
              Register
            </Button>
            
            <Link to={`/events/${event.slug}`}>
              <Button variant="ghost" size="sm" className="text-primary hover:text-white hover:bg-primary/20">
                Details
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Click outside to close share menu */}
      {showShareMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowShareMenu(false)}
        />
      )}
    </motion.div>
  );
};

// Filter Sidebar Component
const FilterSidebar = ({ filters, setFilters, categories, onClear }) => {
  const eventTypes = [
    { value: 'conference', label: 'Conference' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'tournament', label: 'Tournament' },
    { value: 'festival', label: 'Festival' },
    { value: 'training', label: 'Training' },
    { value: 'networking', label: 'Networking' },
    { value: 'exhibition', label: 'Exhibition' },
  ];

  const locations = ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Virtual'];
  const priceRanges = [
    { value: 'free', label: 'Free' },
    { value: '0-5000', label: 'Under KES 5,000' },
    { value: '5000-10000', label: 'KES 5,000 - 10,000' },
    { value: '10000+', label: 'Over KES 10,000' },
  ];

  const hasActiveFilters = filters.eventType || filters.location || filters.priceRange || 
    filters.dateFrom || filters.dateTo;

  return (
    <div className="bg-card-bg border border-border rounded-xl p-5 sticky top-24">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filters
        </h3>
        {hasActiveFilters && (
          <button 
            onClick={onClear}
            className="text-xs text-primary hover:text-primary/80"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Event Type Filter */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-white/70 mb-2">Event Type</label>
        <select
          value={filters.eventType || ''}
          onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value }))}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
          data-testid="filter-event-type"
        >
          <option value="">All Types</option>
          {eventTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {/* Location Filter */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-white/70 mb-2">Location</label>
        <select
          value={filters.location || ''}
          onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
          data-testid="filter-location"
        >
          <option value="">All Locations</option>
          {locations.map(loc => (
            <option key={loc} value={loc}>{loc}</option>
          ))}
        </select>
      </div>

      {/* Date Range Filter */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-white/70 mb-2">Date Range</label>
        <div className="space-y-2">
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
            placeholder="From"
            data-testid="filter-date-from"
          />
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
            placeholder="To"
            data-testid="filter-date-to"
          />
        </div>
      </div>

      {/* Price Range Filter */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-white/70 mb-2">Price Range</label>
        <select
          value={filters.priceRange || ''}
          onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-primary"
          data-testid="filter-price"
        >
          <option value="">Any Price</option>
          {priceRanges.map(range => (
            <option key={range.value} value={range.value}>{range.label}</option>
          ))}
        </select>
      </div>

      {/* Categories (if available) */}
      {categories.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">Category</label>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {categories.map(cat => (
              <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.categories?.includes(cat.id) || false}
                  onChange={(e) => {
                    const cats = filters.categories || [];
                    if (e.target.checked) {
                      setFilters(prev => ({ ...prev, categories: [...cats, cat.id] }));
                    } else {
                      setFilters(prev => ({ ...prev, categories: cats.filter(c => c !== cat.id) }));
                    }
                  }}
                  className="w-4 h-4 rounded text-primary"
                />
                <span className="text-sm text-white/80">{cat.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Events Directory Page
const EventsDirectoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(true);
  
  // Quick registration modal state
  const [quickRegisterEvent, setQuickRegisterEvent] = useState(null);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    eventType: searchParams.get('type') || '',
    location: searchParams.get('location') || '',
    dateFrom: searchParams.get('from') || '',
    dateTo: searchParams.get('to') || '',
    priceRange: searchParams.get('price') || '',
    categories: searchParams.get('categories')?.split(',').filter(Boolean) || []
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0
  });

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      params.append('status', 'published');
      
      if (filters.search) params.append('search', filters.search);
      if (filters.eventType) params.append('event_type', filters.eventType);
      if (filters.location) params.append('city', filters.location);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);

      const response = await fetch(`${API}/universal-events/events?${params}`);
      const data = await response.json();
      
      let filteredEvents = data.events || [];
      
      // Client-side price filtering (since API might not support it)
      if (filters.priceRange) {
        filteredEvents = filteredEvents.filter(event => {
          const lowestPrice = event.ticket_types?.reduce((min, tt) => 
            tt.price < min ? tt.price : min, 
            event.ticket_types[0]?.price || 0
          ) || 0;
          
          switch (filters.priceRange) {
            case 'free':
              return lowestPrice === 0;
            case '0-5000':
              return lowestPrice > 0 && lowestPrice < 5000;
            case '5000-10000':
              return lowestPrice >= 5000 && lowestPrice <= 10000;
            case '10000+':
              return lowestPrice > 10000;
            default:
              return true;
          }
        });
      }
      
      setEvents(filteredEvents);
      setPagination(prev => ({ ...prev, total: data.total || filteredEvents.length }));
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API}/universal-events/categories`);
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, [fetchEvents, fetchCategories]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('q', filters.search);
    if (filters.eventType) params.set('type', filters.eventType);
    if (filters.location) params.set('location', filters.location);
    if (filters.dateFrom) params.set('from', filters.dateFrom);
    if (filters.dateTo) params.set('to', filters.dateTo);
    if (filters.priceRange) params.set('price', filters.priceRange);
    if (filters.categories?.length) params.set('categories', filters.categories.join(','));
    
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const clearFilters = () => {
    setFilters({
      search: '',
      eventType: '',
      location: '',
      dateFrom: '',
      dateTo: '',
      priceRange: '',
      categories: []
    });
  };

  const activeFilterCount = [
    filters.eventType,
    filters.location,
    filters.dateFrom,
    filters.dateTo,
    filters.priceRange,
    ...(filters.categories || [])
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Discover Events
            </h1>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Find and register for conferences, workshops, tournaments, and more across Kenya and beyond.
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Search events by name, location, or keyword..."
                className="w-full bg-zinc-900 border border-zinc-700 rounded-xl pl-12 pr-4 py-4 text-white placeholder-zinc-500 focus:ring-2 focus:ring-primary focus:border-transparent"
                data-testid="events-search-input"
              />
              {filters.search && (
                <button
                  onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  showFilters ? 'bg-primary border-primary text-white' : 'border-border text-white/70 hover:border-white/40'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              
              <span className="text-white/60 text-sm">
                {loading ? 'Loading...' : `${events.length} events found`}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-primary text-white' : 'text-white/60 hover:text-white'
                }`}
                data-testid="view-grid-btn"
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' ? 'bg-primary text-white' : 'text-white/60 hover:text-white'
                }`}
                data-testid="view-list-btn"
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex gap-6">
            {/* Sidebar Filters */}
            <AnimatePresence>
              {showFilters && (
                <motion.aside
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 280, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  className="shrink-0 overflow-hidden"
                >
                  <FilterSidebar
                    filters={filters}
                    setFilters={setFilters}
                    categories={categories}
                    onClear={clearFilters}
                  />
                </motion.aside>
              )}
            </AnimatePresence>

            {/* Events Grid/List */}
            <main className="flex-1">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-card-bg border border-border rounded-xl overflow-hidden animate-pulse">
                      <div className="h-48 bg-white/5" />
                      <div className="p-4 space-y-3">
                        <div className="h-6 bg-white/5 rounded" />
                        <div className="h-4 bg-white/5 rounded w-3/4" />
                        <div className="h-4 bg-white/5 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : events.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="w-16 h-16 mx-auto text-white/20 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No events found</h3>
                  <p className="text-white/60 mb-6">
                    Try adjusting your filters or search query
                  </p>
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className={
                  viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                    : 'space-y-4'
                }>
                  {events.map((event) => (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      viewMode={viewMode}
                      onQuickRegister={setQuickRegisterEvent}
                    />
                  ))}
                </div>
              )}

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <Button
                    variant="outline"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </Button>
                  
                  <span className="px-4 py-2 text-white/60">
                    Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                  </span>
                  
                  <Button
                    variant="outline"
                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Quick Registration Modal */}
      <AnimatePresence>
        {quickRegisterEvent && (
          <QuickRegisterModal
            event={quickRegisterEvent}
            isOpen={!!quickRegisterEvent}
            onClose={() => setQuickRegisterEvent(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventsDirectoryPage;
